import React, { useState, useEffect, useRef } from "react";
import { Box, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import _ from "lodash";
import { useStripe } from "@stripe/react-stripe-js";
import { TopAccountBar } from "../../components/InfoBar/components/TopAccountBar/TopAccountBar.view";
import { useResponsive } from "../../hooks/useResponsive";
import OrderSummary from "./Components/OrderSummary";
import Billing from "./Components/Billing";
import PaymentMethodsSection from "./Components/PaymentMethodsSection";
import OrderTypesSection from "./Components/OrderTypesSection";
import { getPromotion, getOrderTimes, getColors } from "../../utils";
import { useAppSelector, useAppDispatch } from "../../redux/hook";
import { normalizePhoneForPhoneField } from "../../utils/number";
import { updateOrderTime } from "../../redux/actions";
import {
  getPaymentTypes,
  getMaximumPreparationTime,
} from "./PaymentSummary.services";
import { useResponsiveMaterial } from "../../hooks/useResponsive";
import { isQrCode } from "../../utils";
import { navigateWithQr } from "../../utils/routing.utils";
import { useLocation } from "react-router-dom";

const PaymentSummary: React.FC = () => {
  const {
    pendingOrderReducer,
    userReducer,
    configurationReducer,
    shopReducer,
    orderReducer,
    offerReducer,
  } = useAppSelector((s) => s);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const stripe = useStripe();
  const theme = useTheme();
  const location = useLocation();
  const { user } = userReducer;
  const {
    commande,
    totalProductPriceOrder,
    totalPriceOrder,
    managementFee,
    address,
    orderType,
    marketingCampaignPromoAmount,
    freeDelivery,
  } = pendingOrderReducer;
  const { orderTime } = orderReducer;
  const { thresholdOffer } = offerReducer;
  const {
    secondaryColor,
    google,
    configHours,
    servicePrice,
    loyalty,
    theme: themeConfiguration,
  } = configurationReducer;
  const { closed, openHours, specialHours, shopName } = shopReducer;
  const { numero, cards, promotions: promotionsHistory } = user;

  const paymentTypes = getPaymentTypes();
  const normalizedNumero = normalizePhoneForPhoneField(numero);

  const [loading, setLoading] = useState(false);
  const [isPhoneIncorrect, setIsPhoneIncorrect] = useState(false);
  const [phoneObj, setPhoneObj] = useState({
    number: normalizedNumero.replace("+33", ""),
    countryCode: "FR",
    prefix: "+33",
  });
  const [phone, setPhone] = useState(normalizedNumero);
  const [expanded, setExpanded] = useState(false);
  const [paymentType, setSelectedPaymentType] = useState(paymentTypes[0]);
  const [promotion, setPromotion] = useState<number | null>(null);
  const [selected, setSelected] = useState(
    _.find(cards, (c: any) => c.selected)?.id || "",
  );
  const [dateState, setStateRaw] = useState({
    day: moment(),
    hourSelectValue: 0,
    selectTime: false,
    isValidDate: true,
  });
  const [choosePayment, setChoosePayment] = useState(false);
  const [orderTimes, setOrderTimes] = useState([]);
  const { day, hourSelectValue, selectTime, isValidDate } = dateState;
  const { bglightgrey } = getColors();
  const setDateState = (update) =>
    setStateRaw((prev) => ({ ...prev, ...update }));

  const maximumPreparationTime = getMaximumPreparationTime(commande);
  const prevOpenHours = useRef(openHours);
  const prevSpecialHours = useRef(specialHours);
  useResponsive();

  useEffect(() => {
    if (_.isEmpty(commande)) navigateWithQr(navigate, location.pathname, "/");
  }, [commande, navigate]);

  useEffect(() => {
    if (isQrCode()) return;
    const openHoursChanged = !_.isEqual(prevOpenHours.current, openHours);
    const specialHoursChanged = !_.isEqual(
      prevSpecialHours.current,
      specialHours,
    );
    if (openHoursChanged || specialHoursChanged) {
      setDateState({
        day: moment(),
        hourSelectValue: 0,
        selectTime: false,
        isValidDate: true,
      });
      prevOpenHours.current = openHours;
      prevSpecialHours.current = specialHours;
    }
  }, [openHours, specialHours]);
  useEffect(() => {
    (async () => {
      const promotion = await getPromotion();
      setPromotion(promotion);
    })();
  }, [promotionsHistory, loyalty, loyalty, servicePrice, pendingOrderReducer]);

  useEffect(() => {
    if (isQrCode()) return;

    const fetchOrderTimes = async () => {
      setLoading(true);
      const orderTimesRes = await getOrderTimes(day);
      setOrderTimes(orderTimesRes);
      dispatch(
        updateOrderTime(
          !!closed || !orderTimesRes?.length
            ? null
            : (orderTimesRes[0] ?? null),
        ),
      );
      setLoading(false);
    };
    fetchOrderTimes();
  }, [day]);

  if (!commande) {
    navigateWithQr(navigate, location.pathname, "/");
    return null;
  }
  const isMobile = useResponsiveMaterial("xs", "only");
  return (
    <Box sx={{ bgcolor: bglightgrey, minHeight: "100vh" }}>
      <Box
        sx={{
          bgcolor: theme.palette.background.paper,
          position: "sticky",
          top: 0,
          zIndex: 1000,
        }}
      >
        <TopAccountBar />
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 3,
          p: { xs: 4, sm: 10 },
        }}
      >
        <Box sx={{ flex: 2, display: "flex", flexDirection: "column", gap: 3 }}>
          <OrderSummary
            commande={commande}
            totalProductPriceOrder={totalProductPriceOrder}
          />
          <PaymentMethodsSection
            cards={cards}
            selected={selected}
            setSelected={setSelected}
            secondaryColor={secondaryColor}
            paymentType={paymentType}
            setSelectedPaymentType={setSelectedPaymentType}
            dispatch={dispatch}
            isMobile={isMobile}
          />
          {!isQrCode() && (
            <OrderTypesSection
              dispatch={dispatch}
              orderType={orderType}
              address={address}
              phone={phone}
              setPhone={setPhone}
              phoneObj={phoneObj}
              setPhoneObj={setPhoneObj}
              setDateState={setDateState}
              closed={closed}
              googleApiKey={google.apiKey}
              secondaryColor={secondaryColor}
              setIsPhoneIncorrect={setIsPhoneIncorrect}
              isPhoneIncorrect={isPhoneIncorrect}
              day={day}
              hourSelectValue={hourSelectValue}
              orderTimes={orderTimes}
              selectTime={selectTime}
              isValidDate={isValidDate}
              orderTime={orderTime}
              configHours={configHours}
              shopName={shopName}
              isMobile={isMobile}
              theme={themeConfiguration}
            />
          )}
        </Box>
        <Box sx={{ flex: 1, position: "sticky", top: 100 }}>
          <Billing
            card={_.find(cards, "selected")}
            totalProductPriceOrder={totalProductPriceOrder}
            totalPriceOrder={totalPriceOrder}
            managementFee={managementFee}
            promotion={promotion}
            expanded={expanded}
            setExpanded={setExpanded}
            paymentType={paymentType}
            commande={commande}
            selectTime={selectTime}
            setLoading={setLoading}
            isPhoneIncorrect={isPhoneIncorrect}
            setChoosePayment={setChoosePayment}
            phoneObj={phoneObj}
            setDateState={setDateState}
            stripe={stripe}
            loading={loading}
            thresholdOffer={thresholdOffer}
            marketingCampaignPromoAmount={marketingCampaignPromoAmount}
            freeDelivery={freeDelivery}
            orderType={orderType}
            address={address}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default PaymentSummary;
